<template>
    <div :class="['company-types-widget']">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Company Types</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div class="add-company-types-buttons-wrapper">
                    <app-button v-if="!isAddCompanyTypeShown" @click="showAddCompanyTypeSection">
                        Add New
                    </app-button>
                    <app-button v-else @click="hideAddCompanyTypeSection">
                        Cancel
                    </app-button>
                </div>
                <div class="add-company-type-wrapper" v-if="isAddCompanyTypeShown">
                    <div class="flex-wrapper">
                        <h3 class="upper-text">Add New Company Type</h3>
                        <div class="add-company-type-color-wrapper">
                            <div class="add-company-type-color" ref="color" :style="{ 'background-color': companyType.color }" @click="showColorPicker"></div>
                            <color-picker v-if="isColorPickerShown" v-off-click="{ handler: 'hideColorPicker', exclude: ['color'] }" class="add-company-type-color-picker" @input="setCompanyTypeColor($event)" :value="companyType.color" />
                        </div>
                        <app-input
                            v-model="companyType.name" 
                            :label="'Name'" />
                    </div>
                    <app-button @click="saveNewCompanyType" :loading="isSavingNewCompanyType" :disabled="isSavingNewCompanyType">
                        Save Company Type
                    </app-button>
                </div>
                <div class="company-types-wrapper">
                    <div v-for="companyType in companyTypes" :class="['company-type', { 'editing': companyType.isEditShown }]" :key="companyType.id">
                        <div class="company-type-wrapper">
                            <div class="company-type-info">
                                <div class="edit-company-type-color-wrapper">
                                    <div class="edit-company-type-color" @click="companyType.isEditShown ? showEditColorPicker(companyType) : null" :ref="`companyTypeColorPicker${ companyType.id }`" :style="{ 'background-color': companyType.color.edit }"></div>
                                    <color-picker
                                        v-if="companyType.isColorPickerShown"
                                        v-off-click="{ handler: 'hideEditColorPicker', args: [ companyType ], exclude: [`companyTypeColorPicker${ companyType.id }`] }"
                                        class="edit-company-type-color-picker"
                                        @input="updateCompanyTypeColor($event, companyType)"
                                        :value="companyType.color.edit" />
                                </div>
                                <p v-if="!companyType.isEditShown">{{ companyType.name.value }}</p>
                                <app-input
                                    v-else
                                    v-model="companyType.name.edit" 
                                    :label="'Name'" />
                            </div>
                        </div>
                        <div v-if="companyType.isEditShown" class="company-type-edit-buttons-wrapper">
                            <app-button
                                @click="updateCompanyType(companyType)"
                                size="small"
                                :disabled="companyType.isUpdating"
                                :loading="companyType.isUpdating">
                                Save
                            </app-button>
                            <app-button 
                                @click="cancelCompanyTypeEdit(companyType)"
                                size="small"
                                class="red-btn"
                                :disabled="companyType.isUpdating"
                                :loading="companyType.isUpdating">
                                Cancel
                            </app-button>
                        </div>
                        <div v-else class="company-type-edit-buttons-wrapper">
                            <app-button
                                @click="showCompanyTypeEdit(companyType)"
                                size="small"
                                :disabled="companyType.isDeleting">
                                Edit
                            </app-button>
                            <app-button
                                @click="deleteCompanyType(companyType)"
                                class="red-btn"
                                size="small"
                                :disabled="companyType.isDeleting"
                                :loading="companyType.isDeleting">
                                Delete
                            </app-button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" :onClick="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div class="action-container">
                    <!-- <app-button :onClick="steps[step].action" :loading="steps[step].loading" :disabled="isButtonDisabled || steps[step].disabled">
                        {{ steps[step].buttonText }}
                    </app-button> -->
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const AppInput = () => import('@/components/common/app-input.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
import { Sketch } from 'vue-color';

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'company-types-widget',

    props: {
        options: {
            type: Object
        },

        widget: {
            type: Object,
            required: true
        }
    },

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'color-picker': Sketch,
        'widget-close-svg': WidgetCloseSvg
    },

    data() {
        return {
            companyTypes: [],
            isAddCompanyTypeShown: false,
            isColorPickerShown: false,
            isSavingNewCompanyType: false,
            companyType: {
                name: '',
                color: '#FFFFFF'
            },
            clientId: null
        }
    },

    methods: {
        getCompanyTypes() {
            this.$api
                .get(`/service/CompanyTypes/?$filter=ClientId eq ${ this.clientId }`)
                .then(response => {
                    var companyTypeResponse = response.data.value;
                    this.setCompanyTypes(companyTypeResponse);
                })
                .catch(error => {
                    this.$helpers.showFailNotification('An error occurred while getting your company types', { error });
                });
        },

        setCompanyTypes: function (response) {
            this.companyTypes = response.map(companyType => {
                return {
                    id: companyType.Id,
                    name: {
                        value: companyType.Name,
                        edit: companyType.Name
                    },
                    color: {
                        value: companyType.Color,
                        edit: companyType.Color    
                    },
                    isEditShown: false,
                    isColorPickerShown: false,
                    isUpdating: false,
                    isDeleting: false
                };
            });
        },

        // ADD
        showAddCompanyTypeSection() {
            this.isAddCompanyTypeShown = true;
        },

        hideAddCompanyTypeSection() {
            this.isAddCompanyTypeShown = false;
        },

        saveNewCompanyType() {
            this.isSavingNewCompanyType = true;

            var request = {
                Name: this.companyType.name,
                Color: this.companyType.color,
                ClientId: this.clientId
            }

            this.$api
                .post('/api/CompanyTypes/', request)
                .then(response => {
                    this.isSavingNewCompanyType = false;
                    this.isAddCompanyTypeShown = false;

                    this.companyTypes.unshift({
                        id: response.data.Id,
                        name: {
                            value: response.data.Name,
                            edit: response.data.Name
                        },
                        color: {
                            value: response.data.Color,
                            edit: response.data.Color    
                        },
                        isEditShown: false,
                        isColorPickerShown: false,
                        isUpdating: false,
                        isDeleting: false
                    });

                    this.clearCompanyType();
                    EventBus.$emit('company-types-updated');

                })
                .catch(error => {
                    this.isSavingNewCompanyType = false;
                    this.$helpers.showFailNotification('An error occurred while creating your company type.', { error })
                });
        },

        clearCompanyType() {
            this.companyType.name = '';
            this.companyType.color = '#FFFFFF';
        },

        setCompanyTypeColor(color) {
            this.isColorPickerShown = false;
            this.companyType.color = color.hex;
        },

        showColorPicker() {
            this.isColorPickerShown = true;
        },

        hideColorPicker() {
            this.isColorPickerShown = false;
        },

        // EDIT
        updateCompanyType(companyType) {
            companyType.isUpdating = true;

            var changes = [{
                'name': 'Name', 
                'value': companyType.name.edit
            },
            {
                'name': 'Color',
                'value': companyType.color.edit
            }];

            this.$api
                .patch(`/api/CompanyTypes/${ companyType.id }`, changes)
                .then(response => {
                    companyType.isUpdating = false;
                    companyType.isEditShown = false;
                    companyType.color.value = companyType.color.edit;
                    companyType.name.value = companyType.name.edit;
                    companyType.isEditShown = false;
                    EventBus.$emit('company-types-updated');
                })
                .catch(error => {
                    this.isUpdating = false;
                    companyType.isEditShown = false;
                    this.$helpers.showFailNotification('An error occurred while updating your company type.', { error })
                });
        },

        showCompanyTypeEdit(companyType) {
            companyType.isEditShown = true;
        },

        cancelCompanyTypeEdit(companyType) {
            companyType.color.edit = companyType.color.value;
            companyType.name.edit = companyType.name.value;
            this.hideEditColorPicker(companyType);
            companyType.isEditShown = false;
        },

        updateCompanyTypeColor(color, companyType) {
            companyType.color.edit = color.hex;
            this.hideEditColorPicker(companyType);
        },

        showEditColorPicker(companyType) {
            companyType.isColorPickerShown = true;
        },

        hideEditColorPicker(companyType) {
            companyType.isColorPickerShown = false;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        // DELETE

        deleteCompanyType(companyType) {
            companyType.isDeleting = true;

            this.$api
                .delete(`/api/CompanyTypes/${ companyType.id }`)
                .then(response => {
                    companyType.isDeleting = false;
                    var matchedCompanyType = this.companyTypes.find(type => {
                        return type.id === companyType.id;
                    });

                    this.companyTypes.splice(this.companyTypes.indexOf(matchedCompanyType), 1);
                    this.$helpers.showSuccessNotification(`The "${ companyType.name.edit }" company type has been removed.`);
                    EventBus.$emit('company-types-updated');
                })
                .catch(error => {
                    this.isDeleting = false;
                    companyType.isEditShown = false;
                    this.$helpers.showFailNotification(`An error occurred while deleting "${ companyType.name.edit }".`, { error });
                });
        },
    },

    created() {
        this.clientId = this.widget.options.clientId;
        this.getCompanyTypes();
    }
}
</script>

<style scoped>
.company-types-widget .flex-wrapper {
    display: flex;
    align-items: flex-end;
    margin-right: 15px;
}

.company-types-widget .content-wrapper .add-company-types-buttons-wrapper .btn {
    margin-bottom: 25px;
}

.company-types-widget .content-wrapper .add-company-type-wrapper {
    margin-bottom: 25px;
    display: flex;
    align-items: flex-end;
    padding: 25px 15px 15px 15px;
    border: 2px solid #E9E9EF;
    border-radius: 20px;
    position: relative;
}

.company-types-widget .content-wrapper .add-company-type-wrapper .upper-text {
    position: absolute;
    top: -10px;
    left: 15px;
    background: #FFFFFF;
    padding: 0px 15px;
}

.company-types-widget .content-wrapper .add-company-type-wrapper .add-company-type-color-wrapper,
.company-types-widget .content-wrapper .company-type-wrapper .edit-company-type-color-wrapper {
    position: relative;
    width: 30px;
    margin-right: 15px;
}

.company-types-widget .content-wrapper .add-company-type-wrapper .add-company-type-color-wrapper .add-company-type-color,
.company-types-widget .content-wrapper .company-type-wrapper .edit-company-type-color-wrapper .edit-company-type-color {
    height: 30px;
    width: 30px;
    border-radius: 50%;
    border: 2px solid #E9E9EF;
}

.company-types-widget .content-wrapper .add-company-type-wrapper .add-company-type-color-wrapper .add-company-type-color-picker,
.company-types-widget .content-wrapper .company-type-wrapper .edit-company-type-color-wrapper .edit-company-type-color-picker {
    position: absolute;
    top: 15px;
    left: 50%;
    z-index: 1001;
}

.company-types-widget .content-wrapper .company-types-wrapper .company-type {
    padding: 5px;
    border-radius: 20px;
    border: 2px solid #E9E9EF;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
}

.company-types-widget .content-wrapper .company-types-wrapper .company-type .company-type-wrapper,
.company-types-widget .content-wrapper .company-types-wrapper .company-type .company-type-wrapper .company-type-info {
    display: flex;
    align-items: center;
}

.company-types-widget .content-wrapper .company-types-wrapper .company-type .company-type-wrapper .company-type-color {
    height: 20px;
    width: 20px;
    border-radius: 50%;
    margin-right: 10px;
    border: 1px solid #E9E9EF;
}

.company-types-widget .content-wrapper .company-types-wrapper .company-type .company-type-wrapper p {
    color: #877D88;
    font-size: 16px;
    margin-bottom: 0;
}

.company-types-widget .content-wrapper .company-types-wrapper .company-type .company-type-edit-buttons-wrapper {
    display: flex;
}

.company-types-widget .content-wrapper .company-types-wrapper .company-type.editing .company-type-edit-buttons-wrapper {
    flex-direction: column;
}

.company-types-widget .content-wrapper .company-types-wrapper .company-type .company-type-edit-buttons-wrapper .btn:nth-child(1) {
    margin-right: 10px;
}

.company-types-widget .content-wrapper .company-types-wrapper .company-type.editing .company-type-edit-buttons-wrapper .btn:nth-child(1) {
    margin-right: 0;
    margin-bottom: 10px;
}

.company-types-widget .content-wrapper .company-types-wrapper .company-type .company-type-edit-buttons-wrapper .red-btn {
    background: #F30070;
    border: 1px solid #F30070;
    color: #FFFFFF;
}

.company-types-widget .content-wrapper .company-types-wrapper .company-type .company-type-edit-buttons-wrapper .red-btn:hover {
    background: #D10E68;
    border: 1px solid #D10E68;
}
</style>